import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type {
  ContactFormLeadTriggerObject,
  ContactFormTriggerLocation,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { Button } from '../../../ui/Button';
import { ContactForm } from './ContactForm';
import { ContactFormQualifier } from './ContactFormQualifier';
import { useQualifierGate } from './useQualifierGate';
import { useHaloContactSubmission } from './useHaloContactSubmission';
import type { ContactFormMode } from './types';
import * as spokeStyles from './contactFormSpoke.styles';
import * as formStyles from './contactForm.styles';

type ContactFormSpokeProps = {
  onClose: () => void;
  listing?: ListingDetails;
  mode?: ContactFormMode;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
};

export const ContactFormSpoke = ({
  onClose,
  listing: listingProp,
  mode = 'contact',
  triggerLocation,
  triggerObject,
}: ContactFormSpokeProps) => {
  const reduxListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);
  const user = useSelector((state: RootReduxState) => state.user);
  const userInquiryDataCache = useSelector((state: RootReduxState) => state.user?.inquiryDataCache);

  const listing = listingProp || reduxListing;

  if (!listing) {
    return null;
  }

  return (
    <ContactFormSpokeInner
      listing={listing}
      user={user}
      userInquiryDataCache={userInquiryDataCache}
      onClose={onClose}
      mode={mode}
      triggerLocation={triggerLocation}
      triggerObject={triggerObject}
    />
  );
};

type ContactFormSpokeInnerProps = {
  listing: ListingDetails;
  user: RootReduxState['user'];
  userInquiryDataCache: RootReduxState['user']['inquiryDataCache'];
  onClose: () => void;
  mode: ContactFormMode;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject?: ContactFormLeadTriggerObject;
};

const ContactFormSpokeInner = ({
  listing,
  user,
  userInquiryDataCache,
  onClose,
  mode,
  triggerLocation,
  triggerObject,
}: ContactFormSpokeInnerProps) => {
  const { handleSubmitForm, isLoading, serverErrors } = useHaloContactSubmission({
    listing,
    onClose,
    requestToApply: mode === 'requestToApply',
    triggerLocation,
    triggerObject,
  });

  // Qualifier runs only in contact mode; Request-to-Apply skips the gate.
  const gate = useQualifierGate(listing);
  const showQualifier = mode === 'contact' && gate.showQualifier;

  const getSendButtonLabel = () => {
    if (listing.waitlisted) return 'Add me to the waitlist';
    if (mode === 'requestToApply') return 'Send request';
    return 'Send message';
  };
  const sendButtonLabel = getSendButtonLabel();

  // While the qualifier is visible, mark the form + footer `inert` so keyboard
  // and assistive tech can't interact with the gated form underneath.
  // `inert` is valid HTML but not yet in React's DOM types.
  const inertProps = showQualifier ? ({ inert: '', 'aria-hidden': true } as unknown as Record<string, unknown>) : {};

  return (
    <div className={spokeStyles.spokeFormWrapper}>
      <div className={spokeStyles.scrollableContent} {...inertProps}>
        {listing?.displayName && <p className={formStyles.listingName}>{listing.displayName}</p>}
        <div className={spokeStyles.spokeFormContent}>
          <ContactForm
            listing={listing}
            onSubmit={handleSubmitForm}
            isLoading={isLoading}
            serverErrors={serverErrors}
            userInquiryDataCache={userInquiryDataCache}
            user={user}
            mode={mode}
          />
        </div>
      </div>
      {showQualifier && gate.qualifierVariant && (
        <ContactFormQualifier variant={gate.qualifierVariant} listing={listing} onContinue={gate.acknowledge} />
      )}
      <div className={formStyles.formFooter} {...inertProps}>
        <Button
          type="submit"
          form="halo-contact-form"
          shape="rectangle"
          tone="brand"
          emphasis="filled"
          fluid={true}
          disabled={isLoading}
          aria-disabled={isLoading}
        >
          {isLoading ? 'Sending...' : sendButtonLabel}
        </Button>
        <p className={formStyles.disclaimer}>
          By contacting this property, you agree to our{' '}
          <a
            href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms"
            target="_blank"
            rel="noopener noreferrer"
            className={formStyles.disclaimerLink}
          >
            Terms of Use
          </a>
          . Visit our{' '}
          <a
            href="https://www.zillowgroup.com/zg-privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className={formStyles.disclaimerLink}
          >
            Privacy Portal
          </a>{' '}
          for more information. When you click &ldquo;{sendButtonLabel}&rdquo;, we&rsquo;ll send your inquiry to the
          property manager so they can reach out and answer your questions.
        </p>
      </div>
      {isLoading && (
        <div className={spokeStyles.loadingOverlay} aria-hidden="true">
          <div className={spokeStyles.loadingSpinner} />
        </div>
      )}
    </div>
  );
};
